import { ApiError, createAction, RSAAAction } from 'redux-api-middleware';
import Strings from '../../assets/strings';
import { endpointWithVersion } from '../../common/url';
import { State as StoreState } from '../types/State';
import { Dispatch, ThunkAction } from '../types/Store';
import { HTTP_CONTENT_TYPE_HEADER, HTTP_CONTENT_TYPE_JSON, HTTP_GET, HTTP_POST } from './ApiAction';
import { TOSItems, updateMandatoryTos } from './TermsOfService';

// Login [actions]
export const LOGIN = 'barmer-bonus-shared/auth/LOGIN' as const;
export const LOGIN_SUCCESS = 'barmer-bonus-shared/auth/LOGIN_SUCCESS' as const;
export const LOGIN_FAIL = 'barmer-bonus-shared/auth/LOGIN_FAIL' as const;

// Logout [actions]
export const LOGOUT = 'barmer-bonus-shared/auth/LOGOUT' as const;
export const MANUAL_LOGOUT = 'barmer-bonus-shared/auth/MANUAL_LOGOUT' as const;
export const LOGOUT_SUCCESS = 'barmer-bonus-shared/auth/LOGOUT_SUCCESS' as const;
export const LOGOUT_FAIL = 'barmer-bonus-shared/auth/LOGOUT_FAIL' as const;

// Refresh [actions]
export const REFRESH_LOGIN = 'barmer-bonus-shared/auth/REFRESH_LOGIN' as const;
export const REFRESH_LOGIN_SUCCESS = 'barmer-bonus-shared/auth/REFRESH_LOGIN_SUCCESS' as const;
export const REFRESH_LOGIN_FAIL = 'barmer-bonus-shared/auth/REFRESH_LOGIN_FAIL' as const;
export const REFRESH_SESSION = 'barmer-bonus-shared/auth/REFRESH_SESSION' as const;
export const REFRESH_SESSION_SUCCESS = 'barmer-bonus-shared/auth/REFRESH_SESSION_SUCCESS' as const;
export const REFRESH_SESSION_FAIL = 'barmer-bonus-shared/auth/REFRESH_SESSION_FAIL' as const;

export const NOT_AUTHORIZED = 'barmer-bonus-shared/auth/NOT_AUTHORIZED' as const;
export const SET_CSRF_TOKEN = 'barmer-bonus-shared/auth/SET_CSRF_TOKEN' as const;

// We define the above constants again in the Action types since TS cannot detect the different types of constants.
// E.g. type: typeof LOGIN === 'string' and typeof LOGIN_SUCCESS === 'string' as well
// E.g. { type: LOGIN } -> Cannot use string as a type because string is a value. To get the type of a value use typeof.
type LoginAction = { type: typeof LOGIN; payload?: ApiError; error?: boolean };
type LoginSuccessAction = { type: typeof LOGIN_SUCCESS; payload: { result: string; id: string } };
type LoginFailureAction = { type: typeof LOGIN_FAIL; payload: ApiError; error: boolean };

type LogoutAction = {
  type: typeof LOGOUT | typeof MANUAL_LOGOUT;
  payload?: ApiError;
  error?: boolean;
};
type LogoutSuccessAction = { type: typeof LOGOUT_SUCCESS; payload: { result: string } };
type LogoutFailureAction = { type: typeof LOGOUT_FAIL; payload: ApiError; error: boolean };

type NotAuthorizedAction = { type: typeof NOT_AUTHORIZED };
type SetCsrfToken = { type: typeof SET_CSRF_TOKEN; payload: string | null | undefined };

type RefreshLoginAction = { type: typeof REFRESH_LOGIN };
type RefreshLoginSuccessAction = {
  type: typeof REFRESH_LOGIN_SUCCESS;
  payload: { result: string; id: string };
};
type RefreshLoginFailedAction = {
  type: typeof REFRESH_LOGIN_FAIL;
  payload: ApiError;
  error: boolean;
};
type RefreshSessionAction = { type: typeof REFRESH_SESSION; payload?: ApiError; error?: boolean };
type RefreshSessionSuccessAction = {
  type: typeof REFRESH_SESSION_SUCCESS;
  payload: { result: string; id: string };
};
type RefreshSessionFailedAction = {
  type: typeof REFRESH_SESSION_FAIL;
  payload: ApiError;
  error: boolean;
};

export type RequestActionType =
  | typeof LOGIN
  | typeof LOGOUT
  | typeof REFRESH_SESSION
  | typeof REFRESH_LOGIN;

export type FailureActionType =
  | typeof LOGIN_FAIL
  | typeof LOGOUT_FAIL
  | typeof REFRESH_LOGIN_FAIL
  | typeof REFRESH_SESSION_FAIL;

// Here we union join the types onto one type that we export
export type Action =
  | LoginAction
  | LoginSuccessAction
  | LoginFailureAction
  | LogoutAction
  | LogoutSuccessAction
  | LogoutFailureAction
  | NotAuthorizedAction
  | RefreshLoginAction
  | RefreshLoginSuccessAction
  | RefreshLoginFailedAction
  | RefreshSessionAction
  | RefreshSessionSuccessAction
  | RefreshSessionFailedAction
  | SetCsrfToken;

// Authentication current type
export const AUTH_SIGNING_IN = 'signingIn' as const;
export const AUTH_REFRESHING_SESSION = 'refreshing-session' as const;
export const AUTH_SIGNING_OUT = 'signingOut' as const;
export const AUTH_SIGNED_IN = 'signedIn' as const;
export const AUTH_SIGNING_IN_ERROR = 'signingInError' as const;
export const AUTH_SIGNING_OUT_ERROR = 'signingOutError' as const;
export const AUTH_SIGNED_OUT = 'signedOut' as const;

export type SessionState =
  | typeof AUTH_SIGNING_IN
  | typeof AUTH_REFRESHING_SESSION
  | typeof AUTH_SIGNING_OUT
  | typeof AUTH_SIGNED_IN
  | typeof AUTH_SIGNED_OUT
  | typeof AUTH_SIGNING_IN_ERROR
  | typeof AUTH_SIGNING_OUT_ERROR;

export type State = {
  current: SessionState;
  id: string | null | undefined;
  error: ApiError | null | undefined;
  csrfToken: string | null | undefined;
  lastAuthTime: string;
  manualLogout: boolean;
};

// The initial current for the auth reducer
const initialState: State = {
  current: AUTH_SIGNED_OUT,
  id: undefined,
  error: undefined,
  csrfToken: undefined,
  lastAuthTime: '', // ISO Time
  manualLogout: false,
};

// The authentication reducer
export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOGIN:
    case REFRESH_LOGIN:
      return {
        ...state,
        current: AUTH_SIGNING_IN,
        error: undefined,
        id: undefined,
      };
    case REFRESH_SESSION:
      return {
        ...state,
        current: AUTH_REFRESHING_SESSION,
        error: undefined,
        id: undefined,
      };
    case LOGIN_SUCCESS:
    case REFRESH_LOGIN_SUCCESS:
    case REFRESH_SESSION_SUCCESS:
      return {
        ...state,
        error: undefined,
        current: AUTH_SIGNED_IN,
        id: action.payload.id,
        lastAuthTime: new Date().toISOString(),
        manualLogout: false,
      };
    case LOGIN_FAIL:
    case REFRESH_LOGIN_FAIL:
    case REFRESH_SESSION_FAIL:
      return {
        ...state,
        current: AUTH_SIGNING_IN_ERROR,
        error: action.payload,
        id: undefined,
      };
    case LOGOUT:
      return {
        ...state,
        current: AUTH_SIGNING_OUT,
        error: action.payload,
        id: undefined,
      };
    case MANUAL_LOGOUT:
      return {
        ...state,
        current: AUTH_SIGNING_OUT,
        error: action.payload,
        id: undefined,
        manualLogout: true,
      };
    case NOT_AUTHORIZED:
    case LOGOUT_SUCCESS:
      return {
        ...state,
        current: AUTH_SIGNED_OUT,
        error: undefined,
        id: undefined,
        lastAuthTime: '',
      };
    case LOGOUT_FAIL:
      return {
        ...state,
        current: AUTH_SIGNING_OUT_ERROR,
        error: action.payload,
      };
    case SET_CSRF_TOKEN:
      return {
        ...state,
        csrfToken: action.payload,
      };
    default:
      return state;
  }
}

const re = /AUTH-ERROR-\d{3}|BARMER-ID-ERROR-\d{2}/;
export const getGermanErrorMessage = (OIDCErrorMessage?: string): string => {
  if (OIDCErrorMessage === undefined) {
    return Strings.OIDCErrors.unknownError;
  }

  const errorCode = re.exec(OIDCErrorMessage);
  return (
    (errorCode != null && Strings.OIDCErrors[errorCode[0] as keyof typeof Strings.OIDCErrors]) ||
    Strings.OIDCErrors.unknownError
  );
};

export function isLoggedIn(globalState: StoreState): boolean {
  // since we want to refresh the session in the background, we count the refreshing session state as still signed in
  return (
    globalState.auth &&
    (AUTH_SIGNED_IN === globalState.auth.current ||
      AUTH_REFRESHING_SESSION === globalState.auth.current)
  );
}

export function isLoggingIn(globalState: StoreState): boolean {
  return globalState.auth && AUTH_SIGNING_IN === globalState.auth.current;
}

export function isLoggingOut(globalState: StoreState): boolean {
  return globalState.auth && AUTH_SIGNING_OUT === globalState.auth.current;
}

export function isLoggedOut(globalState: StoreState): boolean {
  return globalState.auth && AUTH_SIGNED_OUT === globalState.auth.current;
}

export function isRefreshing(globalState: StoreState): boolean {
  return globalState.auth && AUTH_REFRESHING_SESSION === globalState.auth.current;
}

export const signIn = (
  username: string,
  password: string,
  platform: 'web' | 'mobile',
  version?: string,
): ThunkAction => {
  return (dispatch: Dispatch) => {
    return dispatch(
      createAction({
        endpoint: endpointWithVersion('/login', platform, version),
        types: [
          LOGIN,
          {
            type: LOGIN_SUCCESS,
            payload: (_action: RSAAAction, _state: StoreState, res: Response) =>
              res.json().then((body: TOSItems) => {
                dispatch(updateMandatoryTos(body));
                return body;
              }),
          },
          LOGIN_FAIL,
        ],
        method: HTTP_POST,
        headers: { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON },
        body: JSON.stringify({ username, password }),
        bailout: (state: StoreState): boolean => {
          return isLoggedIn(state);
        },
      }),
    );
  };
};

// Action that should be connected to be dispatched on to the Store.
export const signOut = (manualLogout = false) =>
  createAction({
    endpoint: '/logout',
    types: [manualLogout ? MANUAL_LOGOUT : LOGOUT, LOGOUT_SUCCESS, LOGOUT_FAIL],
    method: HTTP_GET,
    bailout: (state: StoreState): boolean => {
      return isLoggedOut(state) || isLoggingOut(state);
    },
  });

export const notAuthorized = (): Action => {
  return { type: NOT_AUTHORIZED };
};

// Refresh a session with implicit cookie
export const refreshLogin = (platform: string, version?: string): ThunkAction => {
  return (dispatch: Dispatch) => {
    return dispatch(
      createAction({
        endpoint: endpointWithVersion('/refreshtoken', platform, version),
        types: [
          REFRESH_LOGIN,
          {
            type: REFRESH_LOGIN_SUCCESS,
            payload: (_action: RSAAAction, _state: StoreState, res: Response) =>
              res.json().then((body: TOSItems) => {
                dispatch(updateMandatoryTos(body));
                return body;
              }),
          },
          REFRESH_LOGIN_FAIL,
        ],
        method: HTTP_GET,
        bailout: (state: StoreState): boolean => {
          return isLoggingIn(state);
        },
      }),
    );
  };
};

// Refresh a session with implicit cookie while already signed in
export const refreshSession = (platform: string, version?: string) =>
  createAction({
    endpoint: endpointWithVersion('/refreshtoken', platform, version),
    types: [REFRESH_SESSION, REFRESH_SESSION_SUCCESS, REFRESH_SESSION_FAIL],
    method: HTTP_GET,
    bailout: (state: StoreState): boolean => {
      return isRefreshing(state);
    },
  });

export function setCsrfToken(token: string | null | undefined): Action {
  return { type: SET_CSRF_TOKEN, payload: token };
}

export function getCsrfToken(globalState: StoreState): string | null | undefined {
  return globalState.auth.csrfToken;
}
